import cn from 'classnames'
import LabelToggle, {
  LabelToggleOption,
} from 'ext-ui/components/LabelToggle/LabelToggle'
import Processing from 'ext-ui/components/Processing/Processing'
import CheckmarkIcon from 'ext-ui/svg/icon/CheckmarkIcon'
import MagicalAIIcon from 'ext-ui/svg/icon/MagicalAIIcon'
import React, { ReactElement, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import PricingTierCard from 'src/app/Onboarding/PricingTierCard'
import PricingEnterprisePNG from 'src/assets/images/pricing-enterprise.png'
import PricingProPNG from 'src/assets/images/pricing-pro.png'
import PricingStarterPNG from 'src/assets/images/pricing-starter.png'
import Trns, { translateSingleString } from 'src/localization/Trns'
import { UserFeature } from 'src/models/user.models'
import { connectedReplace } from 'src/navigation/Lnk'
import { homeUrl } from 'src/navigation/urls'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import { useQueryCreditCardsPaginated } from 'src/queries/creditCard.queries'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'

import style from './SaasOnboardingPricingTiers.style'

type PlanPeriod = 'MONTHLY' | 'YEARLY'
type PlanType = 'STARTER' | 'PRO' | 'ENTERPRISE'

export type SaasOnboardingPricingTiersProps = {
  className?: string
  onClickPlanButton?: (planPeriod: PlanPeriod, planType: PlanType) => void
  buttonLoadingStates?: Partial<{
    starter: boolean
    pro: boolean
    enterprise: boolean
  }>
}

const EMPTY_LOADING_STATES = {
  starter: false,
  pro: false,
  enterprise: false,
}

export default function SaasOnboardingStepOne(
  props: SaasOnboardingPricingTiersProps,
): ReactElement {
  const {
    className = '',
    buttonLoadingStates: buttonLoadingStateProp = {},
    onClickPlanButton,
  } = props
  const locale = useSelector(selectNullSafeLocaleFromState)
  const buttonLoadingStates = {
    ...EMPTY_LOADING_STATES,
    buttonLoadingStateProp,
  }

  // fetch very first credit card
  const { data: creditCardsData, isLoading: creditCardsLoading } =
    useQueryCreditCardsPaginated({
      filters: {
        sortField: 'createdAt',
        sortDirection: 'ASC',
        count: 1,
      },
      refetchOnMount: 'always',
    })
  const showAstradaFeature =
    creditCardsData?.creditCards.some((cc) =>
      [
        'CNB',
        'CNB-SELF-TEST',
        'CNBTEST',
        'HSBC',
        'HSBC-TEST',
        'HSBC-TEST-LAST-4',
        'TCB',
        'TCB-TEST',
      ].includes(cc.issuer),
    ) ?? false

  const dispatch = useDispatch()

  const { data: featuresData, isFetched: featuresIsFetched } =
    useQueryUserFeatures([UserFeature.SAAS_ENABLED], {})

  const [planPeriod, setPlanPeriod] = useState<PlanPeriod>('YEARLY')

  // REDIRECT TO homeUrl() if SAAS is not enabled (User has HSBC/TCB card)
  useEffect(() => {
    if (featuresIsFetched && !featuresData?.SAAS_ENABLED) {
      dispatch(connectedReplace(homeUrl()))
    }
  }, [featuresIsFetched, featuresData])

  const handlePlanPeriodChange = (selectedValue: LabelToggleOption): void => {
    setPlanPeriod(selectedValue.value as 'MONTHLY' | 'YEARLY')
  }

  if (!featuresIsFetched || creditCardsLoading) {
    return <Processing />
  }

  // Used to prevent a flash of the pricing page if
  // the user enters in an HSBC/TCB card
  if (featuresData?.SAAS_ENABLED === false) {
    return <></>
  }

  return (
    <div className={cn('SassOnboardingStepOne', className)} css={style}>
      <div className="HeadingText-container flex-column align-center justify-center gap-4">
        <h2 className="HeadingText">
          <Trns source="Choose the plan that's right for you" />
        </h2>
        <p className="SubText">
          <Trns source="Find the right fit for your business. Start free or upgrade to a paid plan to unlock more powerful spend controls and expense tracking features." />
        </p>
      </div>
      <div className="LabelToggle-section flex-row justify-center ">
        <LabelToggle
          className="LabelToggle-component"
          options={[
            {
              displayName: translateSingleString(locale, 'Monthly'),
              value: 'MONTHLY',
            },
            {
              displayName: translateSingleString(locale, 'Yearly'),
              value: 'YEARLY',
            },
          ]}
          name="planType"
          onChange={handlePlanPeriodChange}
          value={planPeriod}
        />
      </div>
      <div className="PricingCards-container flex-row align-stretch justify-between gap-8">
        <PricingTierCard
          content={{
            pricingCardAvatar: PricingStarterPNG,
            forWhichBusinesses: 'For small businesses',
            planTitle: 'Starter',
            pricingCardPerUserPrice: (
              <h1 className="PricingCard-per-bigPrice">{'$0'}</h1>
            ),
            perUserText: '/user/month',
            priceDetailsText: 'Maximum 5 users',
            actualPriceText: 'Free',
            buttonText: 'Get Started',
            buttonLoadingState: buttonLoadingStates.starter,
            getStartedHeading: 'Get started with:',
          }}
          onClickPlanButton={(): void => {
            onClickPlanButton?.(planPeriod, 'STARTER')
          }}
          tierFeatures={[
            {
              icon: <CheckmarkIcon />,
              text: 'Up to 5 users',
            },
            {
              icon: <CheckmarkIcon />,
              text: 'Up to 100 virtual cards per month*',
            },
            {
              icon: <CheckmarkIcon />,
              text: '1 expense category and 15 labels',
            },
            {
              icon: <CheckmarkIcon />,
              text: 'Upload receipts',
            },
            {
              icon: <CheckmarkIcon />,
              text: 'Download transaction data and receipts',
            },
          ]}
        />
        <PricingTierCard
          content={{
            pricingCardAvatar: PricingProPNG,
            forWhichBusinesses: 'For small-medium sized businesses',
            planTitle: 'Pro',
            pricingCardPerUserPrice:
              planPeriod === 'YEARLY' ? (
                <h1 className="PricingCard-per-bigPrice">
                  {'$8'}
                  <span className="PricingCard-per-superScript">{'.99'}</span>
                </h1>
              ) : (
                <h1 className="PricingCard-per-bigPrice">
                  {'$11'}
                  <span className="PricingCard-per-superScript">{'.99'}</span>
                </h1>
              ),
            perUserText: '/user/month',
            strikeOutPrice: planPeriod === 'YEARLY' ? 1438 : undefined,
            priceDetailsText: 'Minimum billing: 10 users',
            actualPriceText:
              planPeriod === 'YEARLY' ? '*Billed annually' : undefined,
            buttonText: 'Get Started',
            buttonLoadingState: buttonLoadingStates.pro,
            getStartedHeading: 'Everything in Starter, plus:',
            promoText:
              planPeriod === 'YEARLY'
                ? translateSingleString(locale, 'Save 25% with annual billing')
                : translateSingleString(locale, 'Popular'),
          }}
          onClickPlanButton={(): void => {
            onClickPlanButton?.(planPeriod, 'PRO')
          }}
          variant="highlight"
          tierFeatures={[
            {
              icon: <CheckmarkIcon />,
              text: 'Pay per user',
            },
            {
              icon: <CheckmarkIcon />,
              text: 'Up to 500 virtual cards per month*',
            },
            ...(showAstradaFeature
              ? [
                  {
                    icon: <CheckmarkIcon />,
                    text: 'Physical card transactions feed',
                  },
                ]
              : []),
            {
              icon: <MagicalAIIcon />,
              text: 'Advanced receipt management',
            },
            {
              icon: <MagicalAIIcon />,
              text: 'Expanded expense categories, labels, and automatic coding',
            },
            {
              icon: <CheckmarkIcon />,
              text: 'Automated monthly expense reports',
            },
            {
              icon: <CheckmarkIcon />,
              text: 'QuickBooks Online integration',
            },
          ]}
          aiPowered
        />
        <PricingTierCard
          content={{
            pricingCardAvatar: PricingEnterprisePNG,
            forWhichBusinesses:
              'For large scale businesses who need customization',
            planTitle: 'Enterprise',
            pricingCardPerUserPrice: (
              <h1 className="PricingCard-per-bigPrice">
                <Trns source={'Custom'} />
              </h1>
            ),
            priceDetailsText: 'No user minimum',
            actualPriceText: 'Contact us for pricing',
            buttonText: 'Contact Us',
            buttonLoadingState: buttonLoadingStates.enterprise,
            getStartedHeading: 'Everything in Pro, plus additional options:',
          }}
          onClickPlanButton={(): void => {
            onClickPlanButton?.(planPeriod, 'ENTERPRISE')
          }}
          tierFeatures={[
            {
              icon: <CheckmarkIcon />,
              text: 'Unlimited users',
            },
            {
              icon: <CheckmarkIcon />,
              text: 'Automate virtual card issuance with API key*',
            },
            {
              icon: <MagicalAIIcon />,
              text: 'Unlimited expense categories, labels, and automatic coding',
            },
            {
              icon: <CheckmarkIcon />,
              text: 'Custom reporting and implementation',
            },
            {
              icon: <CheckmarkIcon />,
              text: 'Dedicated account manager',
            },
            {
              icon: <CheckmarkIcon />,
              text: 'NetSuite integration',
            },
          ]}
          aiPowered
        />
      </div>
    </div>
  )
}
